/**
 * Share a case with clients: the person reads the server's anonymized version and confirms it names no client.
 * The confirmation is recorded; any later change to the text, metrics, client name or aliases makes the case
 * internal again until someone confirms anew.
 */

import { useState } from 'react';

import { useAnonymizedCase, useShareCase } from '@/api/queries/cases';
import type { CaseOut } from '@/api/types/cases';
import { ActionButton, buttonState } from '@/ui/ActionButton';
import { Modal } from '@/ui/Modal';
import { FormError } from '@/ui/StateCard';

import { CaseBody } from './CaseBody';

export function ShareCaseDialog({ item, onClose }: { item: CaseOut; onClose: () => void }) {
  const preview = useAnonymizedCase(item.id);
  const share = useShareCase();
  const [reviewed, setReviewed] = useState(false);

  return (
    <Modal
      title={`Share ${item.anonymized_label} with clients`}
      subtitle="Clients, the client PDF and the Advisor's client brief read only this anonymized version."
      size="wide"
      onClose={onClose}
      onSubmit={() => reviewed && share.mutate(item.id, { onSuccess: onClose })}
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <ActionButton state={buttonState(share)} label="Share with clients" disabled={!reviewed || !preview.isSuccess} />
        </>
      }
    >
      <div className="stack-md">
        <FormError error={share.error} />
        {preview.isPending && <p className="hint" role="status">Loading the anonymized version…</p>}
        <FormError error={preview.error} />
        {preview.data && (
          <section className="case-preview" aria-label="Anonymized version">
            <h4>{preview.data.label}</h4>
            <p className="hint">
              {preview.data.industry?.name ?? 'No industry'}, {preview.data.country ?? 'no country'}
            </p>
            <CaseBody content={preview.data} label={`${preview.data.label} (anonymized)`} />
          </section>
        )}
        <label className="check">
          <input type="checkbox" checked={reviewed} disabled={!preview.isSuccess} onChange={(e) => setReviewed(e.target.checked)} />
          <span className="ck-body">
            I reviewed this version: it does not name or identify the client.
            <span className="hint">Any later edit to the text, metrics, client name or aliases makes the case internal again until it is confirmed anew.</span>
          </span>
        </label>
      </div>
    </Modal>
  );
}
